import { site as siteText } from '../i18n/en/site';
import type { MessageKey } from '../i18n';

// Who runs Twinden, for the landing and policy pages (M42,
// docs/landing-page.md). The costs and what's been paid come from the
// server, published in the admin panel (M43, docs/costs-and-contributions.md).

/** Someone shown under "Who we are" on the About page (D118). */
export interface TeamMember {
  name: string;
  /** e.g. "Co-founder". */
  role: string;
  /** A few sentences in your own words. */
  bio: string;
  /**
   * A square photo in client/public/about/, e.g. "/about/deepak.jpg".
   * Without one, their initials are shown.
   */
  photo?: string;
  /** Their role and bio in Hindi, shown when the page is in Hindi (D146). */
  hi?: { role?: string; bio?: string };
}

export const SITE = {
  name: 'Twinden',
  /** Shown in the footer and the policies. */
  legalName: 'Paperdrop Corp',
  /** The footer's "Another project by …" line. */
  company: 'Paperdrop Corp',
  companyUrl: 'https://paperdrop.io',
  /** Courts named in the terms. */
  jurisdiction: 'Pune, Maharashtra, India',
  contact: {
    email: 'hello@paperdrop.io',
    address:
      'Paperdrop Corp, 1/1A/7, Baner Rd, next to Soft Tech Engineers, Baner Gaon, Baner, Pune, Maharashtra 411069, India',
    /** Who handles complaints: the Grievance Officer the IT Rules 2021 ask for (#112). */
    grievanceOfficer: 'Deepak Sharma',
    grievanceEmail: 'deepak@paperdrop.io',
  },
  // The Chip in link (a Razorpay Payment Page, D105) is a server setting,
  // SUPPORT_URL, read through useSupportUrl().
  /**
   * The people behind Twinden, in order, shown on the About page. Empty
   * shows only the company.
   */
  team: [] as TeamMember[],
  /** When the policy pages last changed. */
  policiesUpdated: '1 November 2026',
};

export function formatMoney(amount: number): string {
  const sign = amount < 0 ? '−' : '';
  return `${sign}₹${Math.abs(amount).toLocaleString('en-IN')}`;
}

/** "2026-10" → "October 2026" (or "अक्तूबर 2026" with locale "hi-IN"). */
export function monthName(month: string, locale = 'en-IN'): string {
  const [year, index] = month.split('-').map(Number) as [number, number];
  return new Date(year, index - 1, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });
}

// The admin panel's starting cost items (M43, D110). Their words live in the
// site catalog, so a published month that still uses them word for word can
// be shown in Hindi on the public pages (D146).
type SiteKey = keyof typeof siteText;

const COST_ITEM_KEYS: Array<[label: SiteKey, detail: SiteKey | null]> = [
  ['site.costs.item.appServer', 'site.costs.item.appServerDetail'],
  ['site.costs.item.database', 'site.costs.item.databaseDetail'],
  ['site.costs.item.storage', 'site.costs.item.storageDetail'],
  ['site.costs.item.domain', null],
  ['site.costs.item.developerFees', null],
  ['site.costs.item.office', null],
];

/** What a first month starts with in the admin panel (M43, D110). */
export const DEFAULT_COST_ITEMS = COST_ITEM_KEYS.map(([label, detail]) => ({
  label: siteText[label] as string,
  // The domain's detail is its name, the same in every language.
  detail: detail
    ? (siteText[detail] as string)
    : label === 'site.costs.item.domain'
      ? 'twinden.com'
      : '',
}));

/** The catalog key for a default cost item's English label or detail. */
export const COST_TEXT_KEYS: ReadonlyMap<string, MessageKey> = new Map(
  COST_ITEM_KEYS.flat()
    .filter((key): key is SiteKey => key !== null)
    .map((key) => [siteText[key] as string, key])
);

/** True while a value still holds its "[…]" placeholder. */
export function isPlaceholder(value: string): boolean {
  return /^\[.*\]$/.test(value.trim());
}
